/**
 * SC-255 — is a piece of fixed chrome still inside the region the user can actually see?
 *
 * Zoom detection alone cannot answer that, and the two zoom kinds break chrome in different ways,
 * so there are two tests here rather than one:
 *
 *  - `isRectReachable` catches WebKit visual-viewport magnification, where `position: fixed`
 *    elements stay anchored to the *layout* viewport and slide off-screen wholesale.
 *  - `isOverflowing` catches page zoom, where the CSS viewport shrinks and a cluster whose
 *    children are `flexShrink: 0` becomes wider than its container and clips at both edges.
 *
 * Pure by design: Playwright cannot synthesise trackpad gestures, so this is the part that can
 * be driven from tests with synthetic values.
 */

/** The region the user can see, in *layout* coordinates — the space getBoundingClientRect() reports in. */
export interface VisibleBox {
  left: number
  top: number
  width: number
  height: number
}

export interface ReachabilityVisualViewportLike {
  offsetLeft: number
  offsetTop: number
  width: number
  height: number
}

export interface ReachabilityWindowLike {
  innerWidth: number
  innerHeight: number
}

export interface DOMRectLike {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

export interface OverflowSizeLike {
  scrollWidth: number
  clientWidth: number
}

/**
 * `visualViewport` gives the visible region directly, including the offsets that pinch-panning
 * introduces. Without it (older Firefox, jsdom) the visible region is the whole layout viewport,
 * which is correct for every engine that has no separate visual viewport.
 */
export function getVisibleBox(
  vv: ReachabilityVisualViewportLike | undefined,
  win: ReachabilityWindowLike
): VisibleBox {
  if (!vv) {
    return { left: 0, top: 0, width: win.innerWidth, height: win.innerHeight }
  }
  return { left: vv.offsetLeft, top: vv.offsetTop, width: vv.width, height: vv.height }
}

/**
 * Requires **full containment**, not intersection: "partially not visible" is explicitly the
 * reported bug, and intersection logic would call a half-clipped toolbar fine.
 *
 * Zero-area rects count as reachable. A `display: none` element reports an all-zero rect, and
 * treating that as off-screen would fire the indicator continuously while the chat launcher (or
 * any conditionally rendered control) is hidden.
 */
export function isRectReachable(
  rect: DOMRectLike,
  box: VisibleBox,
  tolerancePx: number
): boolean {
  if (rect.width <= 0 || rect.height <= 0) return true

  return (
    rect.left >= box.left - tolerancePx &&
    rect.top >= box.top - tolerancePx &&
    rect.right <= box.left + box.width + tolerancePx &&
    rect.bottom <= box.top + box.height + tolerancePx
  )
}

/**
 * Children clipped inside their own container. An unrendered element reports `clientWidth` 0 and
 * is not overflowing — same reasoning as the zero-area guard above.
 */
export function isOverflowing(el: OverflowSizeLike, tolerancePx: number): boolean {
  if (el.clientWidth <= 0) return false
  return el.scrollWidth > el.clientWidth + tolerancePx
}
